import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { CampaignProperties } from '../../interface/campaign-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchCampaignsApi, refreshCampaignsApi } from '../../redux/slices/CampaignManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
// import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';

/**
 * Payments Management function Component
 */
function CampaignManagement() {
  const { t } = useTranslation('campaign');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { results, loading } = useAppSelector((state) => state.campaign);

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof CampaignProperties,
    },
    {
      label: t('medium'),
      accessorKey: 'communicationMedium' as keyof CampaignProperties,

      /**
       * Data custom function
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: CampaignProperties) =>
        currentRow.communicationMedium ? (
          <p className="table-row-each-data">{currentRow.communicationMedium?.toUpperCase()}</p>
        ) : (
          <div />
        ),
    },
    {
      label: t('campaignID'),
      accessorKey: 'campaignId' as keyof CampaignProperties,
    },
    {
      label: t('totalRecipients'),
      accessorKey: 'totalAccounts' as keyof CampaignProperties,
    },
    {
      label: t('message'),
      accessorKey: 'template' as keyof CampaignProperties,

      /**
       * Data custom function
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: CampaignProperties) =>
        currentRow.template ? (
          <p className="table-row-each-data">{currentRow.template.message}</p>
        ) : (
          <div />
        ),
    },
    {
      label: t('action'),
      accessorKey: '' as keyof CampaignProperties,
      columnWidth: 60,
      sticky: 'right' as const,
      /**
       * function for action buttons
       * @param {CampaignProperties} currentData - The currentData
       * @returns {any} The result
       */
      customData: (currentData: CampaignProperties): any => (
        <div className="action-button-group">
          <div className="device-listing-actions-wrap">
            <span role="presentation" onClick={() => navigate(currentData.id)}>
              <EyeIcon />
            </span>
          </div>
        </div>
      ),
    },
  ];

  /**
   * Fetch the campaign details in redis
   */
  const fetchCampaigns = () => {
    dispatch(fetchCampaignsApi());
  };

  /**
   * Auto refresh data
   */
  const handleAutoRefresh = () => {
    dispatch(refreshCampaignsApi());
  };

  useEffect(() => {
    dispatch(setPageTitle(t('campaign')));
    if (!results.length) {
      fetchCampaigns();
    }
  }, []);

  return (
    <div className="campaign-management-wrap">
      <div className="campaign-management-header">
        <div className="header-title">
          <MyButton
            label={t('createCampaign')}
            buttonType="submit"
            svgIcon={<PlusIcon />}
            onClickFunc={() => navigate('./create')}
            disableButton={!permissionCheck('cpoCampaignManagementAdd')}
          />
        </div>
      </div>
      <div
        className="table-content"
        style={{
          height: '100%',
        }}>
        <CustomTable
          filterAllow={false}
          header={column}
          data={results}
          loading={loading}
          serialNo
          created
          lastUpdated
          sizeChangeButtonRequired
          disablePagination
          actionButton
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
        />
      </div>
    </div>
  );
}

export default CampaignManagement;
